/* eslint-disable max-len */
/* eslint-disable react/require-default-props */
import React, { useEffect } from 'react';
import proptypes from 'prop-types';

import {
  params, string, arrayOf, number, bool, shape, extend
} from '@thd-nucleus/data-sources';
import { SignInLink } from '@thd-olt-component-react/price';
import { Typography, Alert, Tooltip } from '@one-thd/sui-atomic-components';

import {
  formatPrice,
  getBundleQuantity,
  isAccessoriesSelected
} from '../utils/product-bundle-utils';
import PriceContainer from './subcomponents/price/PriceContainer';
import { AdditionalSavingsContent } from './subcomponents/AdditionalSavingsContent';
import { MajorApplianceDeliveryAndTaxContent } from './subcomponents/MajorApplianceDeliveryAndTaxContent';
import { TOOLTIP_VIEW_DETAILS, MIN_ADVERTISED_PRICE_MESSAGE, MIN_ADVERTISED_PRICE_MESSAGE_SIGN_IN } from '../constants';
import { isBundleUnavailable } from '../utils/product-unavailable-utils';
import { CustomSkeletonLine } from './subcomponents/CustomSkeletonLine';
import useGetPlaceholderHeight from '../hooks/useGetPlaceholderHeight';

const {
  arrayOf: propTypeArrayOf, shape: propTypeShape, string: propTypeString, bool: propTypeBool, number: propTypeNumber
} = proptypes;
/**
 * Component to display a summary of a product bundle, including pricing, delivery, and savings details.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.product - The main product details.
 * @param {Object} props.product.features - Features of the product.
 * @param {string} props.product.features.bundleSummaryTitle - The title for the bundle summary.
 * @param {string} props.product.features.deliveryAndTax - Information about delivery and tax.
 * @param {string} props.product.features.summaryRegularPriceType - The type of regular price summary.
 * @param {boolean} props.product.features.showBundleSavings - Flag to indicate if bundle savings should be shown.
 * @param {Array} props.products - The list of products in the bundle.
 * @param {string} props.itemId - The unique identifier for the item.
 * @param {Object} props.pricingDetails - Details about pricing, delivery, and adjustments.
 * @param {Object} props.pricingDetails.pricing - Pricing information.
 * @param {number} props.pricingDetails.pricing.value - The final price value.
 * @param {Object} props.pricingDetails.delivery - Delivery information.
 * @param {Object} props.pricingDetails.adjustments - Adjustments applied to the pricing.
 * @param {number} props.pricingDetails.adjustments.bundleItemSavings - Savings from individual items in the bundle.
 * @param {number} props.pricingDetails.adjustments.bundleSavings - Total savings for the bundle.
 * @param {Object} props.pricingDetails.mapDetail - Minimum advertised price (MAP) details.
 * @param {number} props.pricingDetails.mapDetail.minAdvertisedPrice - The minimum advertised price.
 * @param {boolean} props.pricingDetails.mapDetail.showMapInfo - Flag to indicate if MAP info should be shown.
 * @param {boolean} props.pricingDetails.mapDetail.showSignInMapAlert - Flag to indicate if a sign-in alert for MAP should be shown.
 * @param {Object} props.pricingDetails.calculations - Calculated pricing details.
 * @param {number} props.pricingDetails.calculations.subTotal - The subtotal price.
 * @param {number} props.pricingDetails.calculations.regularPrice - The regular price.
 * @param {boolean} props.pricingDetails.calculations.hasBundleSavings - Flag to indicate if the bundle has savings.
 * @param {boolean} props.pricingDetails.loading - Flag to indicate if pricing details are loading.
 * @returns {JSX.Element} The rendered BundleSummary component.
 */
const BundleSummary = ({
  product, products, itemId, pricingDetails = null, selectedAccessories
}) => {
  const {
    pricing,
    delivery,
    adjustments,
    mapDetail,
    calculations
  } = pricingDetails || {};
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('bundle-summary.ready');
  }, []);

  const {
    bundleSummaryTitle, deliveryAndTax, summaryRegularPriceType, showBundleSavings, hideBundleSummary
  } = product?.features ?? {};
  const isUnavailable = hideBundleSummary && isBundleUnavailable({ products });

  const { summaryPlaceholderHeight } = useGetPlaceholderHeight(product);

  if (isUnavailable) {
    return null;
  }

  const savingsDetail = 'The amount you save from individual items in this bundle';

  const isAccessoriesIncluded = isAccessoriesSelected(selectedAccessories);

  const regularSummaryText = `${summaryRegularPriceType}${isAccessoriesIncluded ? ' + accessories' : ''}`;

  const bundleQuantity = getBundleQuantity(products);

  const { minAdvertisedPrice, showMapInfo, showSignInMapAlert } = mapDetail ?? {};

  const showMapAlert = showMapInfo && minAdvertisedPrice;

  if (pricingDetails?.loading) {
    return (
      <div
        className="sui-flex sui-flex-col sm:sui-mx-4"
        data-testid="bundle-bundle-summary-placeHolder"
      >
        <CustomSkeletonLine height={summaryPlaceholderHeight} />
      </div>
    );
  }
  const suiRowClasses = 'sui-flex sui-flex-row sui-justify-between sui-mt-1';
  const dashClass = 'sui-w-3 sui-bg-primary sui-border-solid sui-border-strongest sui-border-b-1 sui-mr-5 sui-ml-auto sui-h-4';
  const finalPrice = formatPrice(pricing?.value);

  const { subTotal, regularPrice, hasBundleSavings } = calculations || {};

  return (
    <div
      className="sui-flex sui-flex-col sm:sui-mx-4"
      data-component="BundleSummary"
      data-testid="bundle-summary"
    >
      <Typography variant="body-lg" weight="bold">{bundleSummaryTitle}</Typography>
      <div>
        <div className={suiRowClasses}>
          <Typography variant="body-base">Regular Price ({bundleQuantity} {regularSummaryText})</Typography>
          <PriceContainer minAdvertisedPrice={showMapAlert} price={!!regularPrice} priceType="regular-price">
            <Typography variant="body-base" data-testid="retail-price">
              {formatPrice(regularPrice)}
            </Typography>
          </PriceContainer>
        </div>

        {adjustments?.bundleItemSavings > 0 ? (
          <div className={suiRowClasses}>
            <span className="sui-flex sui-gap-1 sui-items-baseline">
              <span className="sui-flex sui-gap-1 sui-items-center">
                <Typography variant="body-base" color="success">Item Savings</Typography>
              </span>
              {!showMapAlert
                && (
                  <Tooltip offset="normal" title={savingsDetail} data-testid="bundle-savings-tooltip">
                    <Typography
                      variant="body-xs"
                      decoration="underline"
                      data-testid="bundle-view-details"
                    >
                      {TOOLTIP_VIEW_DETAILS}
                    </Typography>
                  </Tooltip>
                )}
            </span>
            <PriceContainer minAdvertisedPrice={showMapAlert} price={!!adjustments?.bundleItemSavings} priceType="product-savings">
              <Typography variant="body-base" color="success" data-testid="product-savings">
                -{formatPrice(adjustments?.bundleItemSavings)}
              </Typography>
            </PriceContainer>
          </div>
        ) : null}

        <div className="sui-flex sui-flex-row sui-justify-between sui-mt-2">
          <Typography variant="body-base">Subtotal</Typography>
          <PriceContainer minAdvertisedPrice={showMapAlert} price={!!subTotal} priceType="subtotal">
            <Typography variant="body-base" data-testid="subtotal">{formatPrice(subTotal)}</Typography>
          </PriceContainer>
        </div>
        <div className="sui-flex sui-w-full sui-justify-end sui-items-center sui-h-4">
          <span className="sui-border-solid sui-border-b-1 sui-mt-3 sui-w-20" />
        </div>
      </div>

      {adjustments?.bundleSavings > 0 ? (
        <AdditionalSavingsContent priceAdjustment={adjustments} showBundleSavings={showBundleSavings} />
      ) : null}

      {deliveryAndTax ? (
        <MajorApplianceDeliveryAndTaxContent deliveryData={delivery} isAccessoriesIncluded={isAccessoriesIncluded} />
      ) : (
        <div className={`${suiRowClasses} sui-items-center`}>
          <Typography variant="body-base">Delivery & Taxes calculated at checkout</Typography>
          <span className="sui-flex sui-flex-col sui-items-center sui-mb-2">
            <span className={dashClass} data-testid="delivery-taxes-dash" />
          </span>
        </div>
      )}
      <div className={`${suiRowClasses} sui-mt-5 sui-border-solid sui-border-t-1 sui-border-button-inactive sui-py-4`}>
        <Typography variant="body-lg" weight="bold">Total</Typography>
        <PriceContainer minAdvertisedPrice={showMapAlert} price={!!finalPrice} priceType="final-price">
          <span className="sui-text-right">
            <Typography variant="h3" component="p" weight="bold" data-testid="finalDiscountedPrice">{finalPrice}</Typography>
            {hasBundleSavings && (
              <>
                <span className="sui-line-through">
                  <Typography variant="body-base" data-testid="retailPrice">
                    {formatPrice(pricing?.original)}
                  </Typography>
                </span>
                <Typography variant="body-base" color="success" data-testid="bundle-total-dollar-off">
                  Save {formatPrice(pricing?.promotion?.dollarOff)} <span>({Math.round(pricing?.promotion?.percentageOff)}%)</span>
                </Typography>
              </>
            )}
          </span>
        </PriceContainer>
      </div>
      {
        showMapAlert && showSignInMapAlert && (
          <Alert status="info">
            <Typography variant="body-base">{MIN_ADVERTISED_PRICE_MESSAGE_SIGN_IN}</Typography>
            <SignInLink itemId={itemId} layoutType="detailed" displayDarkTheme />
          </Alert>
        )
      }
      {
        showMapAlert && !showSignInMapAlert && (<Alert status="info">{MIN_ADVERTISED_PRICE_MESSAGE}</Alert>)
      }
    </div>
  );
};

BundleSummary.propTypes = {
  products: propTypeArrayOf(propTypeShape({
    pricing: propTypeShape(),
    bundleSpecificationDetails: propTypeShape({
      components: propTypeArrayOf(propTypeShape({
        quantity: propTypeNumber
      }))
    })
  })).isRequired,
  itemId: propTypeString.isRequired,
  product: propTypeShape({
    features: propTypeShape({
      bundleSummaryTitle: propTypeString.isRequired,
      priceUnitOfMeasure: propTypeString.isRequired
    })
  }).isRequired,
  pricingDetails: propTypeShape({
    loading: propTypeBool,
    pricing: propTypeShape({
      pricing: propTypeShape({
        original: propTypeNumber.isRequired,
        value: propTypeNumber.isRequired,
        promotion: propTypeShape({
          dollarOff: propTypeNumber.isRequired,
          percentageOff: propTypeNumber.isRequired
        })
      })
    }),
    mapDetail: propTypeShape({
      showMapInfo: propTypeBool.isRequired,
      minAdvertisedPrice: propTypeBool,
      showSignInMapAlert: propTypeBool
    }),
    delivery: propTypeShape({
      deliveryFee: propTypeNumber,
      freeShipping: propTypeBool
    }),
    adjustments: propTypeShape({
      bundleItemSavings: propTypeNumber,
      bundleSavings: propTypeNumber
    }),
    calculations: propTypeShape({
      subtotal: propTypeNumber,
      regularPrice: propTypeNumber,
      hasBundleSavings: propTypeNumber
    })
  }),
  selectedAccessories: propTypeArrayOf(propTypeShape({})).isRequired
};

BundleSummary.displayName = 'BundleSummary';

BundleSummary.dataModel = extend(
  {
    products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf({
      pricing: params({ storeId: string(), isBrandPricingPolicyCompliant: bool() }).shape({
        original: number({ float: true }),
        mapAboveOriginalPrice: bool(),
        mapDetail: shape({
          mapPolicy: string(),
          mapOriginalPriceViolation: bool(),
          mapSpecialPriceViolation: bool()
        }),
        promotion: shape({
          type: string(),
          description: shape({
            shortDesc: string(),
            longDesc: string()
          }),
          dollarOff: number({ float: true }),
          percentageOff: number({ float: true })
        }),
        specialBuy: number({ float: true }),
        value: number({ float: true })
      }),
      identifiers: shape({
        brandName: string(),
        storeSkuNumber: string(),
        productLabel: string(),
        omsThdSku: string(),
        modelNumber: string(),
        canonicalUrl: string()
      })
    })
  }
);

export { BundleSummary };
